import _ from 'underscore';
import { Plan } from 'adready-api/model/plan';
import commonHelper, { inert, isBlank } from 'adready-api/helpers/common';
import accountApi from 'adready-api/api/account';
import advertiserReportsAPI from '@/api/advertiser-reports';
import liStatsApi from '@/api/liStats';
import { ACCOUNT_FLAG_MNI, ACCOUNT_MNI_PREFIX_MERD, IOS_CHANGE_DRAFT } from 'adready-api/constant';
import usersApi, { userRolesApi } from 'adready-api/api/user';
import organizationApi from 'adready-api/api/organization';
import planApi from 'adready-api/api/plans';
import authz from 'adready-api/authz';
import { formatDateForAPI } from '@/util/apiDateFormat';
import {
  COMPARE_RANGE_PREV_DAY,
  KEY_DEMO_ADVERTISER_ID,
  MNI_MOTTO_SOLUTION_ID,
  RANGE_LAST_30_DAYS,
  PAGINATION_DEFAULT_ITEM_PER_PAGE,
  AI_ASSISTANT_FULL_PLATFORM,
} from '@/constant';
import { buildQueryString } from '@/helpers/global/url-helpers';
import EventBus from '@/adready-vue/helpers/global/event-bus';
import config from '@/config';
import { getCampaignDateRange, isDemoInstance, isFlamingoInstance } from '~/util/utility-functions';
import store from '~/store';
import { dedupe } from '~/components/mixins/forklift/dedupe';
import appConsoleDataLayer from '~/api/app-console-data';

const accountLoaders = {
  /**
   * Checks if the given data is available (already loaded and in the store)
   * @param {String} key
   */
  isDataLoaded(key) {
    const data = store.get(key);
    return data && !_.isEmpty(data);
  },

  async loadFlipPixels(pAdvertiser) {
    const key = 'common/universalPixelId';

    EventBus.universalPixelIdReady = false;

    if (!pAdvertiser) {
      store.set(key, null);
      return;
    }
    await appConsoleDataLayer
      .flipPixels(pAdvertiser.id)
      .then((pixels) => {
        if (pixels && _.isArray(pixels)) {
          const universalPixel = pixels.find((p) => p.conversion_type === 'Universal');
          store.set(key, universalPixel ? universalPixel.flip_id : null);
          EventBus.universalPixelIdReady = true;
        }
      })
      .catch(() => {
        store.set(key, null);
        EventBus.universalPixelIdReady = true;
      })
      .finally(() => {
        EventBus.universalPixelIdReady = true;
      });
  },

  async loadAdvertiserGroupings(force, pAccount, pAdvertiser) {
    const key = 'dashboard/filters@ioOptions';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }

    if (!pAccount || !pAdvertiser || isBlank(pAccount.name) || isBlank(pAdvertiser.name)) {
      return [];
    }

    return dedupe(key, () => {
      return advertiserReportsAPI
        .ioNames(
          pAdvertiser.id,
          buildQueryString({
            advertiser: pAdvertiser.name,
            client: pAccount.name,
          })
        )
        .then(async (data) => {
          if (data.length !== 0) {
            const demoAdvertisers = store.get('common/GET_DEMO_ADVERTISER_MAPPING_MAP');
            const advertiserKeys = Object.keys(demoAdvertisers).map((k) => parseInt(k, 10));
            const demoAdvertiserId = parseInt(localStorage.getItem(KEY_DEMO_ADVERTISER_ID), 10);
            const demoAdvertiserMappingArr = advertiserKeys?.includes(demoAdvertiserId)
              ? demoAdvertisers[demoAdvertiserId]
              : demoAdvertisers[1691];
            let demoAdvertiserMapping;
            if (Array.isArray(demoAdvertiserMappingArr) && demoAdvertiserMappingArr.length > 0) {
              demoAdvertiserMapping = demoAdvertiserMappingArr.find((m) => m.solutionId === 1);
              if (!demoAdvertiserMapping) {
                [demoAdvertiserMapping] = demoAdvertiserMappingArr;
              }
            } else {
              demoAdvertiserMapping = undefined;
            }
            if (isDemoInstance() && demoAdvertiserMapping?.selectedCampaign) {
              for (let i = 0; i < data.length; i++) {
                if (demoAdvertiserMapping?.selectedCampaign?.includes(data[i].value)) {
                  data[i].checked = true;
                } else {
                  data[i].checked = false;
                }
              }
            } else {
              for (let i = 0; i < data.length; i++) {
                data[i].checked = data[i].isRecent;
                data[i].disabled = false;
              }
            }
            const adGroupData = await advertiserReportsAPI.adGroupOptions(
              pAdvertiser.id,
              buildQueryString({ includeNonFlip: isFlamingoInstance() })
            );
            if (adGroupData?.data?.length > 0) {
              for (let j = 0; j < adGroupData.data.length; j++) {
                adGroupData.data[j].checked = true;
                adGroupData.data[j].value = adGroupData.data[j].lineItemName;
              }
              store.set('dashboard/filters@adGroupOptions', adGroupData.data);
            }
            store.set(key, data);
            return data;
          }
          store.set(key, []);
          store.set('dashboard/filters@adGroupOptions', []);
          return [];
        })
        .catch((e) => this.forkliftErrHandler(e, 'groupings'));
    });
  },

  async loadEventOptions(force, pAccount, pAdvertiser) {
    const key = 'dashboard/filters@eventOptions';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }

    if (!pAccount || !pAdvertiser) {
      return [''];
    }

    return dedupe(key, () => {
      return advertiserReportsAPI
        .events(
          pAdvertiser.id,
          buildQueryString({
            advertiser: pAdvertiser.name,
            client: pAccount.name,
          })
        )
        .then((data) => {
          data.forEach((element) => {
            element.open = element.selected; // TODO: Amit to review (2 out of 2)
          });
          store.set(key, data);
        })
        .catch((e) => this.forkliftErrHandler(e, 'eventOptions'));
    });
  },

  async loadMediaTypes(force, pAccount, pAdvertiser) {
    const key = 'dashboard/filters@mediaTypeOptions';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }

    if (!pAccount || !pAdvertiser) {
      return [];
    }

    return dedupe(key, () => {
      return advertiserReportsAPI.mediaTypes(
        pAdvertiser.id,
        buildQueryString({
          advertiser: pAdvertiser.name,
          client: pAccount.name,
          includeNonFlip: isFlamingoInstance(),
        })
      );
    })
      .then((data) => {
        if (data && data.length > 0) {
          const newData = [];
          data.forEach((element) => {
            newData.push({ value: element, checked: true });
          });
          store.set(key, newData);
          return data;
        }
        store.set(key, []);
        return [];
      })
      .catch((e) => this.forkliftErrHandler(e, 'mediaTypes'));
  },

  async loadAdvertiserSolutions(force, pAdvertiser) {
    const key = 'common/advertiserSolutions';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }

    if (!pAdvertiser) {
      return [];
    }

    return dedupe(key, () => {
      return appConsoleDataLayer
        .getSolutionsByAdvertiser(pAdvertiser.id)
        .then((data) => {
          store.set(key, data);
          return data;
        })
        .catch((e) => this.forkliftErrHandler(e, 'advertiserSolutions'));
    });
  },

  async loadAdvertiserCampaigns(force, pAccount, pAdvertiser, pSolutionId) {
    const key = 'dashboard/filters@campaignOptions';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }

    if (
      !pAccount ||
      !pAdvertiser ||
      isBlank(pAccount.name) ||
      isBlank(pAdvertiser.name) ||
      !pSolutionId
    ) {
      return [];
    }

    return dedupe(key, () => {
      return appConsoleDataLayer
        .fetchCampaignOptions(pAdvertiser.id, pAdvertiser.name, pAccount.name, pSolutionId)
        .then(async (campaigns) => {
          if (!campaigns.length) {
            store.set(key, []);
            store.set('dashboard/filters@adGroupOptions', []);
            return [];
          }

          // Mark recent campaigns as default checked
          const demoAdvertisers = store.get('common/GET_DEMO_ADVERTISER_MAPPING_MAP');
          const advertiserKeys = Object.keys(demoAdvertisers).map((k) => parseInt(k, 10));
          const demoAdvertiserId = parseInt(localStorage.getItem(KEY_DEMO_ADVERTISER_ID), 10);
          const demoAdvertiserMappingArr = advertiserKeys?.includes(demoAdvertiserId)
            ? demoAdvertisers[demoAdvertiserId]
            : demoAdvertisers[1691];
          let demoAdvertiserMapping;
          if (Array.isArray(demoAdvertiserMappingArr) && demoAdvertiserMappingArr.length > 0) {
            demoAdvertiserMapping = demoAdvertiserMappingArr.find(
              (m) => m.solutionId === pSolutionId
            );
            if (!demoAdvertiserMapping) {
              [demoAdvertiserMapping] = demoAdvertiserMappingArr;
            }
          } else {
            demoAdvertiserMapping = undefined;
          }
          // Mark recent campaigns as default checked
          for (let i = 0; i < campaigns.length; i++) {
            if (isDemoInstance() && demoAdvertiserMapping?.selectedCampaign) {
              if (demoAdvertiserMapping?.selectedCampaign?.includes(campaigns[i].value)) {
                campaigns[i].checked = true;
              } else {
                campaigns[i].checked =
                  campaigns[i].isRecent || pSolutionId === MNI_MOTTO_SOLUTION_ID;
              }
            } else {
              campaigns[i].checked = campaigns[i].isRecent || pSolutionId === MNI_MOTTO_SOLUTION_ID;
            }
          }

          const dates = store.get('dashboard/dates');

          let dateRange;
          if (pSolutionId === MNI_MOTTO_SOLUTION_ID && !isDemoInstance()) {
            // const { rootState } = store;
            // const { dates1 } = rootState.dashboard;
            dateRange = {
              startDate: new Date(new Date().setDate(new Date().getDate() - 31)),
              endDate: new Date(new Date().setDate(new Date().getDate() - 2)),
              compareWith: false,
              compareStartDate: new Date(new Date().setDate(new Date().getDate() - 31)),
              compareEndDate: new Date(new Date().setDate(new Date().getDate() - 3)),
              dateRangeOption: RANGE_LAST_30_DAYS,
              dateCompareOption: COMPARE_RANGE_PREV_DAY,
            };

            store.commit('dashboard/SET_DATES', { ...dates, ...dateRange }, { root: true });
          } else if (isDemoInstance()) {
            dateRange = { ...dates };
          } else {
            dateRange = getCampaignDateRange(campaigns, true);
            const storedDate = sessionStorage.getItem('selectedDates');
            if (storedDate !== undefined && storedDate !== null) {
              store.set('dashboard/dates', JSON.parse(storedDate));
              store.commit(
                'dashboard/SET_DATES',
                { ...dates, ...JSON.parse(storedDate) },
                { root: true }
              );
            }
          }

          // fetch adgroups
          const campaignIds =
            campaigns
              .filter((e) => e.checked)
              ?.map((e) => e.key)
              ?.join(',') || '';
          if (pSolutionId !== MNI_MOTTO_SOLUTION_ID) {
            const adGroupData = await appConsoleDataLayer.fetchAdGroupOptions(
              pAdvertiser.id,
              pSolutionId,
              campaigns
            );
            if (adGroupData?.length > 0) {
              for (let j = 0; j < adGroupData.length; j++) {
                adGroupData[j].checked = true;
                adGroupData[j].value = adGroupData[j].name;
              }
              store.set('dashboard/filters@adGroupOptions', adGroupData);
            }
          }

          if (!dateRange.notAvailable) {
            const payload = {
              advertiser: pAdvertiser ? pAdvertiser.name : '',
              client: pAccount ? pAccount.name : '',
              campaignIds,
              adGroups: '',
              startDate: formatDateForAPI(dateRange?.startDate),
              endDate: formatDateForAPI(dateRange?.endDate),
            };
            const dataMediaType = await appConsoleDataLayer.fetchMediaTypeOptions(
              pAdvertiser.id,
              pSolutionId,
              payload
            );
            const newDataMediaType = [];
            if (dataMediaType.length !== 0) {
              for (let i = 0; i < dataMediaType.length; i++) {
                newDataMediaType.push({ value: dataMediaType[i], checked: true });
              }
              store.set('dashboard/filters@mediaTypeOptions', newDataMediaType);
            }
            const dataAudienceType = await appConsoleDataLayer.fetchAudienceOptions(
              pAdvertiser.id,
              pSolutionId,
              payload
            );
            const newDataAudienceType = [];
            if (dataAudienceType.length > 0) {
              dataAudienceType?.forEach((d) =>
                newDataAudienceType.push({ key: d.value, value: d.key, checked: false })
              );
              store.set('dashboard/filters@audienceOptions', newDataAudienceType);
            }
            const dataCreativeType = await appConsoleDataLayer.fetchCreativeOptions(
              pAdvertiser.id,
              pSolutionId,
              payload
            );
            const newDataCreativeType = [];
            if (dataCreativeType.length) {
              dataCreativeType?.forEach((d) => newDataCreativeType.push({ ...d, checked: false }));
              store.set('dashboard/filters@creativeOptions', newDataCreativeType);
            }

            if (pSolutionId !== MNI_MOTTO_SOLUTION_ID) {
              const dataConversionPixel = await appConsoleDataLayer.fetchConversionPixelOptions(
                pAdvertiser.id,
                pSolutionId,
                payload
              );
              const newDataConversionPixel = [];
              if (dataConversionPixel.length) {
                dataConversionPixel?.forEach((d) =>
                  newDataConversionPixel.push({ ...d, checked: true })
                );
                store.set('dashboard/filters@pixelOptions', newDataConversionPixel);
              }
            }
          } else {
            store.set('dashboard/filters@mediaTypeOptions', []);
            store.set('dashboard/filters@audienceOptions', []);
            store.set('dashboard/filters@creativeOptions', []);
            store.set('dashboard/filters@pixelOptions', []);
          }
          store.set(key, campaigns);
          return campaigns;
        })
        .catch((e) => this.forkliftErrHandler(e, 'campaignOptions'));
    });
  },

  async loadAccount(accountId, force) {
    const key = `common/accountMap@${accountId}`;
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return accountApi
        .account(accountId)
        .then((res) => {
          const currentUser = store.get('common/currentUser');
          if (
            currentUser &&
            authz.isAccountManager(currentUser.role.name) &&
            res.hasFlag(ACCOUNT_FLAG_MNI)
          ) {
            res.name = `${ACCOUNT_MNI_PREFIX_MERD} - ${res.name}`;
          }
          store.set(key, res);
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'account'));
    });
  },

  async loadUserAccounts(userId, force) {
    const key = 'common/userAccounts';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return accountApi
        .userAccount(userId)
        .then((res) => {
          const accounts = res.map((a) => {
            const currentUser = store.get('common/currentUser');
            if (
              currentUser &&
              authz.isAccountManager(currentUser.role.name) &&
              a.hasFlag(ACCOUNT_FLAG_MNI)
            ) {
              a.name = `${ACCOUNT_MNI_PREFIX_MERD} - ${a.name}`;
              return a;
            }
            return a;
          });
          const sortedAccounts = commonHelper.caseInsensitiveSort(accounts, 'name');
          const uniqAccounts = _.uniq(sortedAccounts, true, (a) => a.id);
          store.set(key, uniqAccounts);
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'userAccounts'));
    });
  },

  async loadAccounts(force) {
    const key = 'common/accounts';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return accountApi
        .accountAll()
        .then((res) => {
          const accounts = res.map((a) => {
            const currentUser = store.get('common/currentUser');
            if (
              currentUser &&
              authz.isAccountManager(currentUser.role.name) &&
              a.hasFlag(ACCOUNT_FLAG_MNI)
            ) {
              a.name = `${ACCOUNT_MNI_PREFIX_MERD} - ${a.name}`;
              return a;
            }
            return a;
          });
          const sortedAccounts = commonHelper.caseInsensitiveSort(accounts, 'name');
          store.set(key, sortedAccounts);
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'userAccounts'));
    });
  },

  injectAssistantScripts(user) {
    const hasAIAssistantFullPlatformAccess = user?.userApplications?.some(
      (app) => app.applicationId === AI_ASSISTANT_FULL_PLATFORM
    );
    if (hasAIAssistantFullPlatformAccess) {
      const scripts = [
        {
          src: config.getAIAssistantPlatformScriptURL(),
          id: 'full-assistant-script',
        },
      ];

      scripts.forEach(({ src, id }) => {
        if (!document.getElementById(id)) {
          const script = document.createElement('script');
          script.src = src;
          script.id = id;
          script.async = true;
          document.body.appendChild(script);
        } else {
          console.log(`Script with ID: ${id} already exists in the DOM.`);
        }
      });
    } else {
      const scriptIds = ['full-assistant-script'];
      let doReload = false;

      scriptIds.forEach((id) => {
        const script = document.getElementById(id);
        if (script) {
          doReload = true;
          script.remove();
        }
      });

      document.querySelectorAll('script').forEach((script) => {
        if (script.src.includes('ai/assistant/v2')) {
          doReload = true;
          script.remove();
        }
      });

      if (!doReload) {
        return;
      }

      // catch the error when script is injected manually
      try {
        this.$nextTick(() => {
          // need to refresh to remove loaded scripts in the memory
          window.location.reload(true);
        });
      } catch (e) {
        console.log('Script loaded manually so no need to reload.');
      }
    }
  },

  async loadCurrentUser(userId, force) {
    const key = 'common/currentUser';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return usersApi
        .read(userId)
        .then((res) => {
          store.set(key, res);
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'currentUser'));
    });
  },

  async loadCurrentUserRoles(userId, force) {
    const key = 'common/currentUserRoles';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return userRolesApi
        .globalRoles(userId)
        .then((res) => {
          const currentUserRoles = res.data;
          if (currentUserRoles) {
            const role = currentUserRoles[0];
            store.set(key, role.name);
          }
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'currentUserRoles'));
    });
  },

  async loadUserAccountRoles(userId, force) {
    const key = 'common/userAccountRoles';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return accountApi
        .userAccountRoles(userId)
        .then((res) => {
          store.set(key, res);
          res.forEach((a) => {
            store.set(
              `common/accountRolesMap@${a.accountId}`,
              authz.createRoleMap(res, a.accountId)
            );
          });
          return res;
        })
        .catch((e) => this.forkliftErrHandler(e, 'userAccounts'));
    });
  },

  loadUsers(accountId, force) {
    const key = `common/accountUsersMap@${accountId}`;
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key)); // nothing to do, bail out
    }
    return dedupe(key, () => {
      return usersApi
        .index({ accountId })
        .then((users) => {
          store.set(key, users);
          return users;
        })
        .catch((e) => this.forkliftErrHandler(e, 'users'));
    });
  },

  loadAllUsers(force) {
    const key = 'common/allUsers';
    if (!force && this.isDataLoaded(key)) {
      return []; // nothing to do, bail out
    }
    return dedupe(key, () => {
      return usersApi
        .index({})
        .then((users) => {
          const sortedUser = _.sortBy(users, (u) => u.id).reverse();
          store.set(key, sortedUser);

          return sortedUser;
        })
        .catch((e) => this.forkliftErrHandler(e, 'users'));
    });
  },

  loadAllUsersForCurrentUser(userId, force) {
    const key = 'common/allUsersForCurrentUser';
    if (!force && this.isDataLoaded(key)) {
      return Promise.resolve(store.get(key));
    }
    return dedupe(key, () => {
      return usersApi
        .index({ userId })
        .then((users) => {
          const sortedUser = _.sortBy(users, (u) => u.id).reverse();
          store.set(key, sortedUser);
          return sortedUser;
        })
        .catch((e) => this.forkliftErrHandler(e, 'users'));
    });
  },

  loadAllOrganizations(force) {
    const key = 'common/allOrganizations';
    if (!force && this.isDataLoaded(key)) {
      return []; // nothing to do, bail out
    }
    return dedupe(key, () => {
      return organizationApi
        .index({})
        .then((org) => {
          const sortedOrg = _.sortBy(org, (u) => u.name);
          store.set(key, sortedOrg);

          return sortedOrg;
        })
        .catch((e) => this.forkliftErrHandler(e, 'organizations'));
    });
  },

  loadAccountsData(accountId, force) {
    this.loadAccount(accountId, force);
    return true;
  },

  addCRLinesToList(plans) {
    if (isBlank(plans)) {
      return plans;
    }
    const newPlans = [];
    plans.forEach((p) => {
      newPlans.push(p);
      if (!isBlank(p.crPlans)) {
        const crPlans = p.crPlans
          .map((cr) => new Plan(cr))
          .filter((cr) => cr.statusId === IOS_CHANGE_DRAFT);
        // There should be only one active CR at a point of time, add first one to the list.
        if (!isBlank(crPlans)) {
          newPlans.push(crPlans[0]);
        }
      }
    });
    return newPlans;
  },

  loadPlansData(q, opts, flip, force, includePacing, advertiser, client, xandrId, pixel) {
    const key = 'common/plans';
    if (!force && this.isDataLoaded(key)) {
      return store.get(key); // nothing to do, bail out
    }
    store.dispatch('common/setPlans', { content: [] });
    if (!opts) {
      opts = {};
    }

    const pageState = store.get('common/orderCriteria@page');
    opts.pageNumber = pageState.num || 0;
    opts.perPage = pageState.per || PAGINATION_DEFAULT_ITEM_PER_PAGE;
    const crFilter = false;
    store.set('common/plansQuery', inert(store.get('common/orderCriteria')));
    return dedupe(key, () => {
      return planApi
        .index({ q, ...opts, crFilter, flip, includePacing, advertiser, client, xandrId, pixel })
        .then((response) => {
          store.dispatch('common/setPlans', response);
          return response.content;
        })
        .catch((e) => this.forkliftErrHandler(e, 'plans'));
    });
  },
  loadLineItemStatusCount(accountId, advertiserId, isFlip, force) {
    const key = 'common/liStatusCount';
    if (!force && this.isDataLoaded(key)) {
      return []; // nothing to do, bail out
    }
    const randomNumber = Math.random();
    return dedupe(randomNumber, () => {
      return liStatsApi
        .lineItemStatusCount({
          accountId,
          advertiserId,
          isFlip,
          randomNumber,
        })
        .then((liStatusCount) => {
          const { data } = liStatusCount;
          store.set(key, data);
          return data;
        })
        .catch((e) => this.forkliftErrHandler(e, 'liStatusCount'));
    });
  },
  loadLineItemStatusCountForPlans(accountId, advertiserId, isFlip, force) {
    const key = 'common/liStatusCountForPlans';
    if (!force && this.isDataLoaded(key)) {
      return []; // nothing to do, bail out
    }
    const randomNumber = Math.random();
    const planIdForLineItemStatusCountLoading = 'common/planIdForLineItemStatusCountLoading';
    return dedupe(randomNumber, () => {
      return liStatsApi
        .lineItemStatusCountForPlans({
          accountId,
          advertiserId,
          isFlip,
          randomNumber,
        })
        .then((liStatusCountForPlans) => {
          const { data } = liStatusCountForPlans;
          store.set(key, data);
          store.set(planIdForLineItemStatusCountLoading, null);
          return data;
        })
        .catch((e) => this.forkliftErrHandler(e, 'liStatusCountForPlans'));
    });
  },
};

export default accountLoaders;
